import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from '@/components';
import { AllGroupsCard, BoutiqueCalendar, CreateEventModal, CreateGroupModal, EventTypeLegend, GroupCard } from './components';
import { BoutiqueCalendarLayoutEvent, BoutiqueCalendarVisibleRange } from './components/boutique-calendar';
import {
	API_ERROR_CODE,
	EVENT_CREATED_MESSAGE,
	EVENT_UPDATED_MESSAGE,
	EVENT_DEFAULT_COVER_IMAGE_URL,
	EVENT_ERROR_MESSAGE,
	EXPERT_STUDENTS_PICKER_PAGE_SIZE,
	EXTERNAL_LINK_TARGET,
	HTTP_PREFIX
} from './boutique-classroom.constants';
import { useAcademicBoard, useExpertEventTypes, useExpertEvents, useExpertGroups, useExpertStudents } from './hooks';
import { getEventPaletteTone, resolveGroupGradeId, toEventStartEnd, toStudentPickerItem } from './boutique-classroom.mappers';
import { ALL_EVENT_TYPES_ID, ALL_GROUPS_ID, CreateEventFormValues, CreateGroupFormValues, EventTypeOption, SelectItem, StudentPickerItem } from './boutique-classroom.types';
import { ExpertEventCreateRequestEventPlatformEnum, ExpertEventUpdateRequestEventPlatformEnum } from '@dopingtech/live_expert_bff_api_ts';
import { isWebView, sendMessageToWebView } from '@/utility/webview';
import { PostMessage } from '@/constants/post-message';
import NotificationManager from '@/managers/notification-manager.tsx';
import ExceptionManager from '@/managers/exception-manager';
import { NOTIFICATION_TYPES } from '@/constants/notifications';
import '@/assets/styles/views/boutique-classroom/boutique-classroom.styles.scss';

const ALL_EVENT_TYPES_OPTION: EventTypeOption = { id: ALL_EVENT_TYPES_ID, name: 'Tüm Etkinlik Türleri' };

interface ApiErrorLike {
	response?: { data?: { errors?: Array<{ errorCode?: string }> } };
}

function getApiErrorCode(error: unknown): string | undefined {
	return (error as ApiErrorLike)?.response?.data?.errors?.[0]?.errorCode;
}

const BoutiqueClassroomScreen: React.FC = () => {
	const [selectedGroupId, setSelectedGroupId] = useState<number>(ALL_GROUPS_ID);
	const [selectedEventType, setSelectedEventType] = useState<EventTypeOption>(ALL_EVENT_TYPES_OPTION);

	const { groups, isMutating: isGroupMutating, createGroupFromStudents, addExpertToGroup } = useExpertGroups();
	const { students } = useExpertStudents({ size: EXPERT_STUDENTS_PICKER_PAGE_SIZE });
	const { eventTypes, isLoading: isEventTypesLoading } = useExpertEventTypes();
	const { experts: academicBoardExperts, branches: academicBoardBranches, isLoading: isAcademicBoardLoading } = useAcademicBoard();
	const pickerStudents = useMemo<StudentPickerItem[]>(() => students.map(student => toStudentPickerItem(student, groups)), [groups, students]);

	const [visibleRange, setVisibleRange] = useState<BoutiqueCalendarVisibleRange | null>(null);
	const eventGroupIds = useMemo(() => (selectedGroupId === ALL_GROUPS_ID ? undefined : [selectedGroupId]), [selectedGroupId]);
	const {
		events,
		isFetching: isEventsFetching,
		createEvent,
		isCreating: isEventCreating,
		updateEvent,
		isUpdating: isEventUpdating
	} = useExpertEvents({
		from: visibleRange?.from,
		to: visibleRange?.to,
		groupIds: eventGroupIds
	});

	const coloredEvents = useMemo<BoutiqueCalendarLayoutEvent[] | typeof events>(
		() =>
			events.map(event => {
				const typeIndex = eventTypes.findIndex(type => type.id === event.eventTypeId);
				return typeIndex >= 0 ? { ...event, variant: getEventPaletteTone(typeIndex) } : event;
			}),
		[eventTypes, events]
	);

	const eventTypeOptions = useMemo<EventTypeOption[]>(() => [ALL_EVENT_TYPES_OPTION, ...eventTypes.map(type => ({ id: type.id, name: type.name }))], [eventTypes]);

	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [editingEvent, setEditingEvent] = useState<BoutiqueCalendarLayoutEvent | null>(null);

	const groupOptions = useMemo<SelectItem[]>(() => groups.map(group => ({ id: group.id, name: group.name })), [groups]);

	const handleCreateEvent = useCallback(() => {
		setEditingEvent(null);
		setIsCreateModalOpen(true);
	}, []);
	const handleEditEvent = useCallback((event: BoutiqueCalendarLayoutEvent) => {
		setEditingEvent(event);
		setIsCreateModalOpen(true);
	}, []);
	const handleCloseCreateModal = useCallback(() => {
		setIsCreateModalOpen(false);
		setEditingEvent(null);
	}, []);

	const handleSubmitCreateEvent = useCallback(
		async (values: CreateEventFormValues) => {
			if (!values.eventType || !values.date || !values.startTime || !values.endTime || !values.platform) return;
			if (editingEvent) {
				const { start: updatedStart, end: updatedEnd } = toEventStartEnd(values.date, values.startTime, values.endTime);
				const updated = await updateEvent({
					eventId: editingEvent.id,
					request: {
						name: values.name.trim(),
						shortDescription: values.shortDescription.trim() || undefined,
						eventStartDate: updatedStart,
						eventEndDate: updatedEnd,
						eventPlatform: values.platform.id as ExpertEventUpdateRequestEventPlatformEnum,
						participationLink: values.participationLink.trim() || undefined
					}
				});
				if (!updated) return;
				NotificationManager.showMessage(NOTIFICATION_TYPES.SUCCESS, EVENT_UPDATED_MESSAGE);
				setIsCreateModalOpen(false);
				setEditingEvent(null);
				return;
			}
			const groupIds = values.groups.map(group => Number(group.id));

			const expertId = values.expert?.id;
			const groupsWithoutExpert = expertId === undefined ? [] : groups.filter(group => groupIds.includes(group.id) && !group.experts.some(expert => expert.id === expertId));

			for (const group of groupsWithoutExpert) {
				try {
					await addExpertToGroup(group.id, expertId as number, values.branch?.id);
				} catch (error) {
					const errorCode = getApiErrorCode(error);
					if (errorCode !== API_ERROR_CODE.MEMBER_ALREADY_ACTIVE) {
						if (errorCode === API_ERROR_CODE.EXPERT_NOT_IN_ACADEMIC_BOARD) {
							NotificationManager.showMessage(NOTIFICATION_TYPES.ERROR, EVENT_ERROR_MESSAGE.EXPERT_NOT_IN_ACADEMIC_BOARD);
						} else {
							ExceptionManager.errorHandler(error);
						}
						return;
					}
				}
			}

			const { start, end } = toEventStartEnd(values.date, values.startTime, values.endTime);
			const result = await createEvent({
				eventTypeId: values.eventType.id,
				name: values.name.trim(),
				shortDescription: values.shortDescription.trim() || undefined,
				coverImageUrl: EVENT_DEFAULT_COVER_IMAGE_URL,
				eventStartDate: start,
				eventEndDate: end,
				eventPlatform: values.platform.id as ExpertEventCreateRequestEventPlatformEnum,
				participationLink: values.participationLink.trim() || undefined,
				groupIds: groupIds.length ? groupIds : undefined,
				additionalStudentIds: groupIds.length === 0 && values.studentIds.length ? values.studentIds : undefined
			});
			if (!result) return;

			const skippedCount = result.data?.data?.skippedStudentIds?.length ?? 0;
			if (skippedCount > 0) {
				NotificationManager.showMessage(NOTIFICATION_TYPES.WARNING, `${skippedCount} ${EVENT_ERROR_MESSAGE.SKIPPED_STUDENTS}`);
			}
			NotificationManager.showMessage(NOTIFICATION_TYPES.SUCCESS, EVENT_CREATED_MESSAGE);
			setIsCreateModalOpen(false);
		},
		[addExpertToGroup, createEvent, editingEvent, groups, updateEvent]
	);

	const [groupModalStudents, setGroupModalStudents] = useState<StudentPickerItem[] | null>(null);
	const { students: ungroupedStudents, isLoading: isUngroupedStudentsLoading } = useExpertStudents({
		size: EXPERT_STUDENTS_PICKER_PAGE_SIZE,
		withoutGroup: true,
		enabled: groupModalStudents !== null
	});
	const ungroupedPickerStudents = useMemo<StudentPickerItem[]>(() => ungroupedStudents.map(student => toStudentPickerItem(student, groups)), [groups, ungroupedStudents]);
	const handleOpenCreateGroup = useCallback((selectedStudents: StudentPickerItem[]) => setGroupModalStudents(selectedStudents), []);
	const handleCloseCreateGroup = useCallback(() => setGroupModalStudents(null), []);
	const handleSubmitCreateGroup = useCallback(
		async (values: CreateGroupFormValues) => {
			const gradeId = resolveGroupGradeId([...students, ...ungroupedStudents].filter(student => values.studentIds.includes(student.id)));
			const result = await createGroupFromStudents({ name: values.name, gradeId, studentIds: values.studentIds });
			if (result) setGroupModalStudents(null);
		},
		[createGroupFromStudents, students, ungroupedStudents]
	);

	const handleJoinEvent = useCallback((event: BoutiqueCalendarLayoutEvent) => {
		if (!event.joinUrl) {
			NotificationManager.showMessage(NOTIFICATION_TYPES.WARNING, EVENT_ERROR_MESSAGE.JOIN_LINK_MISSING);
			return;
		}
		const link = /^https?:\/\//i.test(event.joinUrl) ? event.joinUrl : `${HTTP_PREFIX}${event.joinUrl}`;
		if (isWebView()) {
			sendMessageToWebView({ type: PostMessage.openExternalLink, data: { link } });
			return;
		}
		window.open(link, EXTERNAL_LINK_TARGET);
	}, []);

	return (
		<div className="boutique-classroom">
			<div className="boutique-classroom__top">
				<div className="boutique-classroom__heading">
					<h1 className="boutique-classroom__title">Takvim</h1>
					<p className="boutique-classroom__description">Gruplarınızdaki öğrencilerin derslerini, görüşmelerinizi ve tüm etkinliklerinizi tek takvimde yönetin.</p>
				</div>
				<div className="boutique-classroom__actions">
					<button
						type="button"
						className="boutique-classroom__create"
						onClick={handleCreateEvent}>
						<i className="icons-plus" />
						<span>Etkinlik Oluştur</span>
					</button>
				</div>
			</div>

			<div className="boutique-classroom__groups">
				<AllGroupsCard
					groupCount={groups.length}
					isActive={selectedGroupId === ALL_GROUPS_ID}
					onSelect={() => setSelectedGroupId(ALL_GROUPS_ID)}
				/>
				{groups.map(group => (
					<GroupCard
						key={group.id}
						group={group}
						isActive={selectedGroupId === group.id}
						onSelect={setSelectedGroupId}
					/>
				))}
			</div>

			<div className="boutique-classroom__filters">
				<EventTypeLegend eventTypes={eventTypes} />
				<div className="boutique-classroom__filter-actions">
					<Dropdown<EventTypeOption>
						className="boutique-classroom__event-type"
						options={eventTypeOptions}
						selectedOption={selectedEventType}
						onOptionSelect={setSelectedEventType}
						size="small"
						menuAlign="right"
					/>
				</div>
			</div>

			<div className="boutique-classroom__calendar">
				<BoutiqueCalendar
					eventList={coloredEvents}
					groupList={groups}
					isLoading={isEventsFetching}
					onEventJoin={handleJoinEvent}
					onEventEdit={handleEditEvent}
					onVisibleRangeChange={setVisibleRange}
				/>
			</div>

			<CreateEventModal
				isOpen={isCreateModalOpen}
				onClose={handleCloseCreateModal}
				onSubmit={handleSubmitCreateEvent}
				editingEvent={editingEvent}
				eventTypes={eventTypes}
				branches={academicBoardBranches}
				experts={academicBoardExperts}
				groups={groupOptions}
				students={pickerStudents}
				onCreateGroup={handleOpenCreateGroup}
				isSubmitting={isEventCreating || isEventUpdating || isGroupMutating}
				isLoadingOptions={isEventTypesLoading || isAcademicBoardLoading}
			/>
			<CreateGroupModal
				isOpen={groupModalStudents !== null}
				onClose={handleCloseCreateGroup}
				onSubmit={handleSubmitCreateGroup}
				students={groupModalStudents ?? undefined}
				availableStudents={ungroupedPickerStudents}
				isLoadingStudents={isUngroupedStudentsLoading}
				isSubmitting={isGroupMutating}
			/>
		</div>
	);
};

export default BoutiqueClassroomScreen;
